<script lang="ts" setup generic="T extends GenericObject">
import { provide } from 'vue';
import { CELL_CONTEXT } from '../shared';
import type { GenericObject } from '../../../../shared/types';
import type { UiDataTableColumn } from '../interfaces';

type Row = T;
type Column = UiDataTableColumn<T>;

interface Props {
  column?: Column;
  row?: Row;
  value?: string;
  rawValue?: any;
  loading?: boolean;
}

const props = defineProps<Props>();

provide(CELL_CONTEXT, {
  align: props.column?.align ?? 'start',
  monospace: props.column?.monospace ?? false,
  value: props.value ?? '',
  rawValue: props.rawValue,
});
</script>

<template>
  <div :class="[$style.root, { [$style.loading]: props.loading }]">
    <div v-if="props.loading" :class="$style.loader">
      <div :class="$style.loaderInner"></div>
    </div>
    <div :class="$style.content">
      <slot />
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../shared' as tableSharedStyles;

.root {
  position: relative;
  box-sizing: border-box;
  width: auto;
  font-size: 13px;
  line-height: 16px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-right: 1px solid tableSharedStyles.$border-color;
    z-index: tableSharedStyles.$z-index-border;
    pointer-events: none;
  }
}

.content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading .content {
  opacity: 0;
}

.loader {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  padding: 12px;
  z-index: tableSharedStyles.$z-index-clickable-cell + 1;
  box-sizing: border-box;
}

.loaderInner {
  position: relative;
  overflow: hidden;
  height: 100%;
  width: 100%;
  background: #f5f5f5;
  border-radius: 4px;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 0;

    background: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0));
    animation: wave 1.5s linear 0.5s infinite;
  }
}

@keyframes wave {
  0% {
    transform: translate(-100%);
  }

  to {
    transform: translate(100%);
  }
}
</style>
